import { Link, useRouteError } from 'react-router-dom';

/**
 * RouteError — React Router `errorElement`. ROUND8 UAT-C03 fix.
 *
 * Previamente el `errorElement` del AppShell era `<NotFound />` — eso
 * enmascaraba CUALQUIER error de render (ej. un `.filter()` sobre un
 * shape inesperado del backend) como un "404 No encontramos esa página"
 * engañoso. El operator veía 404 tras login porque MyTickets throweaba
 * en render, no porque la ruta no existiera.
 *
 * Ahora distinguimos:
 *  - Error con status 404 (Response thrown por RR cuando no matchea ruta)
 *    → pantalla 404 real.
 *  - Cualquier otro error (render throw, loader fail) → "Algo salió mal"
 *    con botón Recargar — NO miente diciendo 404.
 *
 * Las rutas genuinamente inexistentes siguen cubiertas por el catch-all
 * `{ path: '*', element: <NotFound /> }`.
 */
export function RouteError(): JSX.Element {
  const error = useRouteError();

  const status =
    error != null && typeof error === 'object' && 'status' in error
      ? (error as { status?: number }).status
      : undefined;

  if (status === 404) {
    return (
      <section className="container-fc py-20 text-center">
        <h1 className="font-display text-5xl text-fc-ink">404</h1>
        <p className="mt-4 text-fc-muted">No encontramos esa página.</p>
        <Link to="/" className="mt-6 inline-block underline hover:text-fc-ink">
          Volver al inicio
        </Link>
      </section>
    );
  }

  return (
    <section className="container-fc py-20 text-center" role="alert">
      <h1 className="font-display text-4xl text-fc-ink">Algo salió mal</h1>
      <p className="mx-auto mt-4 max-w-md text-fc-muted">
        Ocurrió un error inesperado al cargar esta sección. Probá recargar la página; si el problema
        sigue, volvé más tarde.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-card bg-primary px-4 py-2 text-sm font-medium text-fc-bg shadow-sm transition-colors hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          Recargar página
        </button>
        <Link
          to="/"
          className="inline-flex items-center rounded-card border border-neutral-200 bg-neutral-100 px-4 py-2 text-sm text-fc-ink transition-colors hover:bg-neutral-200"
        >
          Volver al inicio
        </Link>
      </div>
    </section>
  );
}
